import { errorWords } from "@alm/domain";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { KEEP_PLACE, optionalSearch } from "@/lib/search";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import type { Permission } from "@alm/policy";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Plus } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCanOrganizationWide } from "@/lib/policy";
import { CustomRolesTable, type RoleRow } from "./-components/custom-roles-table";
import { NativeRoles } from "./-components/native-roles";
import { SearchField } from "@/components/app/patterns/search-field";

export const Route = createFileRoute("/_authenticated/_administration/settings/roles/")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: optionalSearch(search.q),
  }),
  component: RolesPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** Every role the organization can give: the three built in, then its own. Editing happens on a
 * page of its own, because a role is a hundred decisions and a dialog is the wrong size for a
 * hundred of anything. */
function RolesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { q } = Route.useSearch();
  const navigateSearch = useNavigate({ from: Route.fullPath });
  const { text: search, change: setSearch } = useSearchParamText(
    q,
    (next) =>
      void navigateSearch({
        search: (prev) => ({ ...prev, q: next }),
        ...KEEP_PLACE,
      }),
  );
  const [removing, setRemoving] = useState<RoleRow | null>(null);
  /* A role reaches everybody holding it at every building, so changing one is organization-wide. */
  const canEdit = useCanOrganizationWide("organization:manage-roles");

  const roles = useQuery(orpc.administration.roles.list.queryOptions({}));

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.roles.key() });
  const duplicate = useMutation(
    orpc.administration.roles.duplicate.mutationOptions({
      onSuccess: async (copy) => {
        toast.success(`Copied as ${copy.name}`);
        await refresh();
        await navigate({ to: "/settings/roles/$roleId", params: { roleId: copy.id } });
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.administration.roles.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Role deleted");
        setRemoving(null);
        await refresh();
      },
      onError: fail,
    }),
  );

  const all = useMemo<RoleRow[]>(
    () =>
      (roles.data ?? []).map((role) => ({
        ...role,
        permissions: role.permissions as Permission[],
      })),
    [roles.data],
  );
  const native = all.filter((role) => role.isSystemRole);
  const rows = useMemo<RoleRow[]>(() => {
    const needle = search.trim().toLowerCase();
    return all.filter(
      (role) =>
        !role.isSystemRole &&
        (!needle ||
          role.name.toLowerCase().includes(needle) ||
          (role.description ?? "").toLowerCase().includes(needle)),
    );
  }, [all, search]);

  return (
    <TooltipProvider>
      <div className="space-y-8">
        <PageHeader
          title="Roles & permissions"
          meta="A role is a named set of permissions. Give it to a person from their record, and they can do exactly what it holds."
          actions={
            canEdit ? (
              <Button asChild>
                <Link to="/settings/roles/new" search={{}}>
                  <Plus className="size-4" />
                  New role
                </Link>
              </Button>
            ) : null
          }
        />

        {native.length > 0 ? (
          <NativeRoles
            roles={native}
            place="organization"
            copying={duplicate.isPending}
            onCopy={canEdit ? (role) => duplicate.mutate({ roleId: role.id }) : undefined}
          />
        ) : null}

        <section className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold">This organization's roles</h2>
              <p className="text-muted-foreground text-sm">
                Yours to shape. Copy a native role or begin blank.
              </p>
            </div>
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Find a role…"
              className="w-64 max-w-full"
            />
          </div>

          {roles.isPending ? (
            <Skeleton className="h-48 w-full" />
          ) : roles.isError ? (
            <ReadFailed
              message="The roles could not be loaded."
              error={roles.error}
              retrying={roles.isFetching}
              onRetry={() => void roles.refetch()}
            />
          ) : rows.length > 0 ? (
            <CustomRolesTable
              roles={rows}
              canEdit={canEdit}
              onCopy={(role) => duplicate.mutate({ roleId: role.id })}
              onDelete={setRemoving}
            />
          ) : search ? (
            <EmptyState title="No role matches" description={`Nothing is called “${search}”.`} />
          ) : (
            <EmptyState
              title="No custom roles yet"
              description="What anybody may do is the roles held at their buildings. Copy a native role to adjust it, or make one for a job none of them describes."
            />
          )}
        </section>

        {removing ? (
          <RemoveConfirm
            title={`Delete ${removing.name}?`}
            description="Nobody holds it, so nothing changes for anyone. It cannot be brought back."
            onConfirm={() => remove.mutate({ roleId: removing.id })}
            onClose={() => setRemoving(null)}
          />
        ) : null}
      </div>
    </TooltipProvider>
  );
}
